import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import { isPlatformLevel, can } from '../utils/roleUtils';

// Accent palette for the invoice PDF. '' = Default (each template's built-in color).
// Includes the existing template colors (Red = Modern) plus the tenant palette.
const INVOICE_COLORS = [
  { color: '', label: 'Default' },
  { color: '#B23B3B', label: 'Red' },
  { color: '#2d3436', label: 'Charcoal' },
  { color: '#1e3a5f', label: 'Navy' },
  { color: '#0E6B6B', label: 'Teal' },
  { color: '#1a5632', label: 'Forest' },
  { color: '#4a1942', label: 'Plum' },
  { color: '#2c2c54', label: 'Indigo' },
];

const SKIP_FEE_PHRASE = 'I understand';

const InvoiceConfig = ({ currentUser }) => {
  const navigate = useNavigate();
  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);
  const canEdit = can(currentUser, 'settings', 'write');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenter, setSelectedTypingCenter] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [skipFeeDialogOpen, setSkipFeeDialogOpen] = useState(false);
  const [skipFeePhrase, setSkipFeePhrase] = useState('');

  // Invoice config state (only invoice-specific settings)
  const [config, setConfig] = useState({
    invoicePrefix: 'INV',
    invoiceNumberPadding: 5,
    currency: 'AED',
    vatRate: 5.00,
    paymentTerms: 'Payment due upon receipt',
    footerNote: '',
    invoiceTemplate: 'CLASSIC',
    invoiceBankAccountId: '',
    invoiceColor: '',
    invoiceFont: 'HELVETICA',
    showFooter: true,
    showSignature: true,
    showCustomerColumn: true,
    showTicketNumber: true,
    svcChargeLabel: 'Svc Chg',
    skipGovtFeePayment: false
  });

  // Fetch typing centers
  useEffect(() => {
    const fetchTypingCenters = async () => {
      try {
        const response = await ApiService.getTypingCenters();
        if (response.data?.data) {
          setTypingCenters(response.data.data);
          if (response.data.data.length > 0) {
            if (currentUser?.typingCenterId) {
              setSelectedTypingCenter(currentUser.typingCenterId);
            } else {
              setSelectedTypingCenter(response.data.data[0].id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching typing centers:', err);
        setError('Failed to load typing centers');
      }
    };
    fetchTypingCenters();
  }, [currentUser]);

  // Fetch config + bank accounts when typing center changes
  useEffect(() => {
    if (selectedTypingCenter) {
      fetchConfig();
      fetchBankAccounts();
    }
  }, [selectedTypingCenter]);

  const fetchBankAccounts = async () => {
    try {
      const response = await ApiService.getTcBankAccounts(selectedTypingCenter);
      const list = response.data?.data || response.data || [];
      setBankAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setBankAccounts([]);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getInvoiceConfig(selectedTypingCenter);
      if (response.data?.data) {
        const data = response.data.data;
        setConfig({
          invoicePrefix: data.invoice_prefix || 'INV',
          invoiceNumberPadding: data.invoice_number_padding || 5,
          currency: data.currency || 'AED',
          vatRate: data.vat_rate || 5.00,
          paymentTerms: data.payment_terms || 'Payment due upon receipt',
          footerNote: data.footer_note || '',
          invoiceTemplate: data.invoice_template || 'CLASSIC',
          invoiceBankAccountId: data.invoice_bank_account_id || '',
          invoiceColor: data.invoice_color || '',
          invoiceFont: data.invoice_font || 'HELVETICA',
          showFooter: data.show_footer !== undefined ? data.show_footer : true,
          showSignature: data.show_signature !== undefined ? data.show_signature : true,
          showCustomerColumn: data.show_customer_column !== undefined ? data.show_customer_column : true,
          showTicketNumber: data.show_ticket_number !== undefined ? data.show_ticket_number : true,
          svcChargeLabel: data.svc_charge_label || 'Svc Chg',
          skipGovtFeePayment: data.skip_govt_fee_payment === true
        });
      }
    } catch (err) {
      console.error('Error fetching invoice config:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load invoice configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveConfig = async (next) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.setInvoiceConfig(selectedTypingCenter, next);
      setSuccess('Invoice settings saved successfully');
      return true;
    } catch (err) {
      console.error('Error saving invoice config:', err);
      setError(err.response?.data?.message || 'Failed to save invoice settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => saveConfig(config);

  // The skip-fee switch saves at once: turning it on only after the typed confirmation, turning it off directly.
  const applySkipFee = async (value) => {
    setConfig(prev => ({ ...prev, skipGovtFeePayment: value }));
    if (!(await saveConfig({ ...config, skipGovtFeePayment: value }))) {
      setConfig(prev => ({ ...prev, skipGovtFeePayment: !value }));
    }
  };

  const handleSkipFeeToggle = (event) => {
    if (event.target.checked) {
      setSkipFeePhrase('');
      setSkipFeeDialogOpen(true);
    } else {
      applySkipFee(false);
    }
  };

  const confirmSkipFee = () => {
    setSkipFeeDialogOpen(false);
    applySkipFee(true);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const response = await ApiService.previewInvoiceConfigPdf(selectedTypingCenter, config);
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview PDF');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading && !selectedTypingCenter) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Shown as Settings › Documents › Invoices; the tab names the page. */}

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Info Alert — guide to related settings */}
      <Alert severity="info" sx={{ mb: 2 }}>
        To update your company details and logo, go to{' '}
        <Box
          component="span"
          sx={{ fontWeight: 'bold', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
          onClick={() => navigate('/settings?section=company&tab=details')}
        >
          Company details
        </Box>
        . To configure invoice email delivery, go to{' '}
        <Box
          component="span"
          sx={{ fontWeight: 'bold', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
          onClick={() => navigate('/settings?section=communication&tab=email')}
        >
          Email settings
        </Box>
        .
      </Alert>

      {/* Typing Center Selector */}
      {isPlatform && typingCenters.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Typing Center</InputLabel>
            <Select
              value={selectedTypingCenter}
              label="Select Typing Center"
              onChange={(e) => setSelectedTypingCenter(e.target.value)}
            >
              {typingCenters.map(tc => (
                <MenuItem key={tc.id} value={tc.id}>
                  {tc.name} ({tc.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={3}>
                Invoice Number Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Invoice Prefix"
                    value={config.invoicePrefix}
                    onChange={handleChange('invoicePrefix')}
                    disabled={!canEdit}
                    fullWidth
                    helperText="e.g., INV, INV/MAIN/"
                    placeholder="INV"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number Padding"
                    value={config.invoiceNumberPadding}
                    onChange={handleChange('invoiceNumberPadding')}
                    disabled={!canEdit}
                    fullWidth
                    type="number"
                    InputProps={{ inputProps: { min: 1, max: 10 } }}
                    helperText="Number of digits (e.g., 5 = 00001)"
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={600} mt={4} mb={3}>
                Invoice Template
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={config.invoiceTemplate}
                      label="Template"
                      onChange={handleChange('invoiceTemplate')}
                      disabled={!canEdit}
                    >
                      <MenuItem value="CLASSIC">Classic (signatures & partner logos)</MenuItem>
                      <MenuItem value="MODERN">Modern (compact, with bank details)</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Applies to all invoices for this typing center
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={config.invoiceTemplate !== 'MODERN'}>
                    <InputLabel>Bank account on invoice</InputLabel>
                    <Select
                      value={config.invoiceBankAccountId}
                      label="Bank account on invoice"
                      onChange={handleChange('invoiceBankAccountId')}
                      disabled={!canEdit}
                    >
                      <MenuItem value="">
                        <em>Default account</em>
                      </MenuItem>
                      {bankAccounts.map(acc => (
                        <MenuItem key={acc.id} value={acc.id}>
                          {acc.account_name} - {acc.bank_name}{acc.is_default ? ' (default)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Shown in the footer of the Modern template
                    </Typography>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Font</InputLabel>
                    <Select
                      value={config.invoiceFont}
                      label="Font"
                      onChange={handleChange('invoiceFont')}
                      disabled={!canEdit}
                    >
                      <MenuItem value="HELVETICA">Helvetica (default)</MenuItem>
                      <MenuItem value="TIMES">Times</MenuItem>
                      <MenuItem value="COURIER">Courier</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Used throughout the generated PDF
                    </Typography>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Service charge column label"
                    value={config.svcChargeLabel}
                    onChange={handleChange('svcChargeLabel')}
                    disabled={!canEdit}
                    fullWidth
                    placeholder="Svc Chg"
                    helperText='Header for the "Svc Chg" column on the invoice line-item table'
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.showFooter}
                        onChange={handleChange('showFooter')}
                        disabled={!canEdit}
                        color="primary"
                      />
                    }
                    label="Show footer logo strip"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.showSignature}
                        onChange={handleChange('showSignature')}
                        disabled={!canEdit}
                        color="primary"
                      />
                    }
                    label="Show signature block"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.showCustomerColumn}
                        onChange={handleChange('showCustomerColumn')}
                        disabled={!canEdit}
                        color="primary"
                      />
                    }
                    label="Show customer column"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.showTicketNumber}
                        onChange={handleChange('showTicketNumber')}
                        disabled={!canEdit}
                        color="primary"
                      />
                    }
                    label="Show ticket number"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Accent color
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Applied to the table header (and the Modern template's title). "Default" keeps each template's built-in color.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {INVOICE_COLORS.map(({ color, label }) => {
                      const isSelected = (config.invoiceColor || '') === color;
                      return (
                        <Box
                          key={label}
                          onClick={canEdit ? () => setConfig(prev => ({ ...prev, invoiceColor: color })) : undefined}
                          sx={canEdit
                            ? { cursor: 'pointer', textAlign: 'center', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.05)' } }
                            : { textAlign: 'center' }}
                        >
                          <Box sx={{
                            width: 44, height: 44, borderRadius: '50%',
                            bgcolor: color || 'transparent',
                            backgroundImage: color ? 'none' : 'linear-gradient(135deg, #e0e0e0 45%, transparent 45%, transparent 55%, #e0e0e0 55%)',
                            border: isSelected ? '3px solid' : '2px solid',
                            borderColor: isSelected ? 'success.main' : (color ? 'transparent' : '#bdbdbd'),
                            boxShadow: isSelected ? '0 0 0 2px rgba(115,191,105,0.3)' : 'none',
                            mx: 'auto', mb: 0.5,
                          }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.skipGovtFeePayment}
                        onChange={handleSkipFeeToggle}
                        disabled={!canEdit || saving}
                        color="error"
                      />
                    }
                    label="Skip government fee payment before finalising"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                    Off by default. When on, a draft invoice can be finalised without recording its government fee payments.
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={600} mt={4} mb={3}>
                Invoice Terms & Notes
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Payment Terms"
                    value={config.paymentTerms}
                    onChange={handleChange('paymentTerms')}
                    disabled={!canEdit}
                    fullWidth
                    placeholder="Payment due upon receipt"
                    helperText="Terms displayed on invoice"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Footer Note"
                    value={config.footerNote}
                    onChange={handleChange('footerNote')}
                    disabled={!canEdit}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Thank you for your business!"
                    helperText="Optional text displayed at bottom of invoice"
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  startIcon={previewing ? <CircularProgress size={20} /> : <PdfIcon />}
                  onClick={handlePreview}
                  disabled={previewing || saving}
                  sx={{ minWidth: 160 }}
                >
                  {previewing ? 'Generating...' : 'Preview Test PDF'}
                </Button>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ minWidth: 150 }}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Preview / Info Panel */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Invoice Number Preview
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <Typography variant="h6" fontFamily="monospace">
                  {config.invoicePrefix}{String(1).padStart(config.invoiceNumberPadding || 5, '0')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Example: First invoice number
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                The invoice number format combines your prefix with a padded sequence number.
              </Typography>

              <Typography variant="body2" color="text.secondary">
                <strong>Examples:</strong><br />
                INV-00001<br />
                INV/MAIN/00001<br />
                TC-INV-0001
              </Typography>
            </Paper>

            {/* Quick links removed: the note at the top already links to company details and email settings. */}
          </Grid>
        </Grid>
      )}

      <Dialog open={skipFeeDialogOpen} onClose={() => setSkipFeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Skip government fee payment?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Your accounting will not be correct if you turn this on.
          </Alert>
          <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
            <li>Government fees on invoices won&apos;t be recorded as paid from your bank, cash or card.</li>
            <li>Your bank and cash balances in the app won&apos;t match your real accounts, and reconciliation will show differences.</li>
            <li>The unrecorded fees collect in &quot;Unreconciled Disbursements&quot; for your accountant.</li>
            <li>VAT and profit are not affected.</li>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <strong>{SKIP_FEE_PHRASE}</strong> to confirm.
          </Typography>
          <TextField
            value={skipFeePhrase}
            onChange={(e) => setSkipFeePhrase(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipFeeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmSkipFee}
            disabled={skipFeePhrase !== SKIP_FEE_PHRASE || saving}
          >
            I understand, skip government fee payments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceConfig;
